"use client";

import dynamic from "next/dynamic";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { FiArrowLeft, FiCheck, FiCrosshair, FiKey, FiLayers, FiMapPin, FiPlus, FiRefreshCw, FiSearch, FiX } from "react-icons/fi";
import { Button } from "@/components/UI/Button/Button";
import { KIND_LABELS, KYIV_BOUNDS, KYIV_CENTER, LANDMARK_KINDS, ROAD_MIN_ZOOM,
    type Bounds, type Landmark, type LandmarkInput, type LandmarkKind, type MapData,
    type NavigationStatus, type NearbyRoad, type StreetResult } from "@/lib/navigation/types";
import { validateCoordinates, validateLandmark } from "@/lib/navigation/validation";
import { editorHeaders, errorMessage, navigationRequest } from "./client";
import { LandmarkEditor } from "./LandmarkEditor";
import { NavigationTabs } from "./NavigationTabs";
import type { MapCommand } from "./NavigationMap";
import cls from "./Navigation.module.css";

const NavigationMap = dynamic(() => import("./NavigationMap"), {
    ssr: false, loading: () => <div className={cls.mapLoading} role="status">Завантажуємо карту Києва…</div>,
});
const EMPTY_MAP: MapData = {
    roads: { type: "FeatureCollection", features: [] }, landmarks: { type: "FeatureCollection", features: [] },
    roadsTruncated: false, landmarksTruncated: false,
};
const TOKEN_KEY = "udata-navigation-editor";
const count = (value: number | undefined) => value === undefined ? "—" : value.toLocaleString("uk-UA");

export function NavigationLab() {
    const [token, setToken] = useState("");
    const [keyInput, setKeyInput] = useState("");
    const [accessOpen, setAccessOpen] = useState(false);
    const [authBusy, setAuthBusy] = useState(false);
    const [authError, setAuthError] = useState("");
    const [status, setStatus] = useState<NavigationStatus | null>(null);
    const [statusError, setStatusError] = useState("");
    const [data, setData] = useState<MapData>(EMPTY_MAP);
    const [viewport, setViewport] = useState<{ bounds: Bounds; zoom: number }>({ bounds: KYIV_BOUNDS, zoom: 11.5 });
    const [loading, setLoading] = useState(true);
    const [dataError, setDataError] = useState("");
    const [mapError, setMapError] = useState("");
    const [refresh, setRefresh] = useState(0);
    const [kind, setKind] = useState<LandmarkKind | "">("");
    const [verification, setVerification] = useState("");
    const [showRoads, setShowRoads] = useState(true);
    const [showVerified, setShowVerified] = useState(true);
    const [draft, setDraft] = useState<LandmarkInput | null>(null);
    const [selected, setSelected] = useState<Landmark | null>(null);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectionLoading, setSelectionLoading] = useState(false);
    const [formError, setFormError] = useState("");
    const [placing, setPlacing] = useState<"create" | "move" | null>(null);
    const [nearby, setNearby] = useState<NearbyRoad[]>([]);
    const [nearbyLoading, setNearbyLoading] = useState(false);
    const [nearbyError, setNearbyError] = useState("");
    const [command, setCommand] = useState<MapCommand | null>(null);
    const [query, setQuery] = useState("");
    const [streets, setStreets] = useState<StreetResult[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState("");
    const [listQuery, setListQuery] = useState("");
    const [message, setMessage] = useState("");
    const keyRef = useRef<HTMLInputElement>(null);
    const selectionRequest = useRef<AbortController | null>(null);
    const canEdit = status?.canEdit ?? false;

    useEffect(() => {
        try { setToken(sessionStorage.getItem(TOKEN_KEY) ?? ""); } catch { /* Editing also works without session storage. */ }
        return () => selectionRequest.current?.abort();
    }, []);
    useEffect(() => { if (accessOpen) keyRef.current?.focus(); }, [accessOpen]);
    useEffect(() => {
        if (!dirty) return;
        const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [dirty]);
    useEffect(() => {
        if (!message) return;
        const timer = setTimeout(() => setMessage(""), 7000);
        return () => clearTimeout(timer);
    }, [message]);

    useEffect(() => {
        const controller = new AbortController();
        navigationRequest<NavigationStatus>("/status", { signal: controller.signal, headers: editorHeaders(token) })
            .then(result => { setStatus(result); setStatusError(""); })
            .catch(error => { if (!controller.signal.aborted) { setStatus(null); setStatusError(errorMessage(error)); } });
        return () => controller.abort();
    }, [token, refresh]);

    useEffect(() => {
        const controller = new AbortController();
        const timer = setTimeout(async () => {
            setLoading(true);
            const params = new URLSearchParams({ bbox: viewport.bounds.join(","), zoom: String(viewport.zoom) });
            if (kind) params.set("kind", kind);
            if (verification) params.set("verified", verification);
            try {
                const result = await navigationRequest<MapData>(`/map?${params}`, { signal: controller.signal });
                if (!controller.signal.aborted) { setData(result); setDataError(""); }
            } catch (error) {
                if (!controller.signal.aborted) { setData(EMPTY_MAP); setDataError(errorMessage(error)); }
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        }, 200);
        return () => { controller.abort(); clearTimeout(timer); };
    }, [viewport, kind, verification, refresh]);

    const latitude = draft?.latitude;
    const longitude = draft?.longitude;
    useEffect(() => {
        setNearby([]); setNearbyError(""); setNearbyLoading(false);
        try { validateCoordinates(longitude, latitude); } catch { return; }
        setNearbyLoading(true);
        const controller = new AbortController();
        const timer = setTimeout(async () => {
            setNearbyLoading(true);
            try {
                const result = await navigationRequest<{ roads: NearbyRoad[] }>(`/roads/nearby?lng=${longitude}&lat=${latitude}`, { signal: controller.signal });
                if (!controller.signal.aborted) setNearby(result.roads);
            } catch (error) {
                if (!controller.signal.aborted) setNearbyError(errorMessage(error));
            } finally {
                if (!controller.signal.aborted) setNearbyLoading(false);
            }
        }, 300);
        return () => { controller.abort(); clearTimeout(timer); };
    }, [longitude, latitude]);

    useEffect(() => {
        setStreets([]); setSearchError(""); setSearchLoading(false);
        if (query.trim().length < 3) return;
        const controller = new AbortController();
        const timer = setTimeout(async () => {
            setSearchLoading(true);
            try {
                const result = await navigationRequest<{ streets: StreetResult[] }>(`/roads/search?q=${encodeURIComponent(query.trim())}`, { signal: controller.signal });
                if (!controller.signal.aborted) setStreets(result.streets);
            } catch (error) {
                if (!controller.signal.aborted) setSearchError(errorMessage(error));
            } finally {
                if (!controller.signal.aborted) setSearchLoading(false);
            }
        }, 350);
        return () => { controller.abort(); clearTimeout(timer); };
    }, [query]);

    const onViewport = useCallback((bounds: Bounds, zoom: number) => setViewport({ bounds, zoom }), []);
    function discardAllowed() { return !dirty || window.confirm("Відкинути незбережені зміни орієнтира?"); }

    async function unlock(event: FormEvent) {
        event.preventDefault();
        setAuthBusy(true); setAuthError("");
        const value = keyInput.trim();
        try {
            const result = await navigationRequest<NavigationStatus>("/status", { headers: editorHeaders(value) });
            if (!result.canEdit) throw new Error("Ключ не підходить. Перевірте NAVIGATION_EDITOR_TOKEN на сервері.");
            setToken(value); setStatus(result); setKeyInput(""); setAccessOpen(false);
            try { sessionStorage.setItem(TOKEN_KEY, value); } catch { /* Keep the key in memory for this session. */ }
            setMessage("Редагування увімкнено.");
        } catch (error) { setAuthError(errorMessage(error)); }
        finally { setAuthBusy(false); }
    }

    function startCreating() {
        if (!canEdit) { setAccessOpen(true); return; }
        if (saving || !discardAllowed()) return;
        selectionRequest.current?.abort(); setSelectionLoading(false);
        setDraft(null); setSelected(null); setDirty(false); setFormError(""); setPlacing("create");
    }

    function changeDraft(patch: Partial<LandmarkInput>) {
        setDraft(current => current ? { ...current, ...patch,
            ...("longitude" in patch || "latitude" in patch ? { verified: false, bindings: [] } : {}),
        } : null);
        setDirty(true); setFormError("");
    }

    function selectPosition(lng: number, lat: number) {
        if (!canEdit || saving) return;
        try { validateCoordinates(lng, lat); } catch (error) { setMessage(errorMessage(error)); return; }
        const position = { longitude: Number(lng.toFixed(6)), latitude: Number(lat.toFixed(6)) };
        if (placing === "create") {
            setDraft({ ...position, kind: "traffic_light", title: "", signCode: "", signValue: "", notes: "",
                verified: false, viewHeading: null, accuracyMeters: null, bindings: [] });
            setSelected(null); setDirty(true); setFormError("");
        } else if (draft) changeDraft(position);
        setPlacing(null);
    }

    async function selectLandmark(id: string, fly = false) {
        if (saving || !discardAllowed()) return;
        selectionRequest.current?.abort();
        const controller = new AbortController(); selectionRequest.current = controller;
        setSelectionLoading(true);
        try {
            const { landmark } = await navigationRequest<{ landmark: Landmark }>(`/landmarks/${id}`, { signal: controller.signal });
            if (controller.signal.aborted) return;
            setSelected(landmark); setDraft(landmark); setDirty(false); setPlacing(null); setFormError("");
            if (fly) setCommand({ sequence: Date.now(), center: [landmark.longitude, landmark.latitude], zoom: 17 });
        } catch (error) {
            if (!controller.signal.aborted) setMessage(errorMessage(error));
        } finally {
            if (!controller.signal.aborted) setSelectionLoading(false);
        }
    }

    function selectRoad(id: string) {
        const road = data.roads.features.find(feature => feature.properties.id === id)?.properties;
        if (!road) return;
        if (!draft || !canEdit) { setMessage(`${road.name || "Ділянка без назви"} · ${Math.round(road.lengthMeters)} м. Прив’язки доступні в картці орієнтира.`); return; }
        if (saving) return;
        if (draft.bindings.some(binding => binding.edgeId === id)) return;
        if (draft.bindings.length >= 6) { setFormError("Можна прив’язати до 6 ділянок."); return; }
        changeDraft({ bindings: [...draft.bindings, { edgeId: id, direction: road.oneWay === -1 ? "backward" : "forward" }] });
    }

    function closeEditor() {
        if (saving || !discardAllowed()) return;
        selectionRequest.current?.abort(); setSelectionLoading(false);
        setDraft(null); setSelected(null); setDirty(false); setPlacing(null); setFormError("");
    }

    async function save() {
        if (!draft || !canEdit || saving) return;
        setFormError("");
        let input: LandmarkInput;
        try { input = validateLandmark(draft, !!selected); } catch (error) { setFormError(errorMessage(error)); return; }
        setSaving(true);
        try {
            const result = await navigationRequest<{ landmark: Landmark }>(selected ? `/landmarks/${selected.id}` : "/landmarks", {
                method: selected ? "PATCH" : "POST", headers: editorHeaders(token), body: JSON.stringify(input),
            });
            setSelected(result.landmark); setDraft(result.landmark); setDirty(false); setPlacing(null);
            setCommand({ sequence: Date.now(), center: [result.landmark.longitude, result.landmark.latitude], zoom: Math.max(viewport.zoom, 16) });
            setRefresh(value => value + 1); setMessage("Орієнтир збережено в базі даних.");
        } catch (error) { setFormError(errorMessage(error)); }
        finally { setSaving(false); }
    }

    async function remove() {
        if (!selected || !canEdit || saving) return;
        setSaving(true); setFormError("");
        try {
            await navigationRequest(`/landmarks/${selected.id}`, { method: "DELETE", headers: editorHeaders(token), body: JSON.stringify({ version: selected.version }) });
            setDraft(null); setSelected(null); setDirty(false); setPlacing(null);
            setRefresh(value => value + 1); setMessage("Орієнтир видалено з карти.");
        } catch (error) { setFormError(errorMessage(error)); }
        finally { setSaving(false); }
    }

    const visibleLandmarks = data.landmarks.features.map(feature => feature.properties).filter(landmark =>
        `${landmark.title} ${KIND_LABELS[landmark.kind]} ${landmark.signCode} ${landmark.bindings.map(binding => binding.roadName).join(" ")}`.toLowerCase().includes(listQuery.toLowerCase()));

    return <main className={cls.page} lang="uk">
        <NavigationTabs active="editor" />
        <header className={cls.intro}>
            <div>
                <p className={cls.eyebrow}>UData Lab <span>/</span> Навігація без GPS</p>
                <h1>Карта <span>орієнтирів.</span></h1>
                <p className={cls.description}>Оцифровуємо Київ: дороги, знаки та світлофори.<br />Кожен перевірений об’єкт — основа майбутньої локалізації.</p>
            </div>
            <div className={cls.introAside}>
                <span className={cls.stageBadge}>Етап 01 · Карта й розмітка</span>
                <button type="button" className={cls.primaryButton} disabled={!status?.ready || saving} onClick={startCreating}><FiPlus /> Додати орієнтир</button>
                <button type="button" className={cls.textButton} onClick={() => setAccessOpen(value => !value)}><FiKey /> {canEdit ? "Доступ редактора" : "Увести ключ редактора"}</button>
            </div>
        </header>

        {accessOpen && <section className={cls.accessPanel} aria-label="Доступ редактора">
            <div><h2>{canEdit ? "Редагування увімкнено" : "Ключ редактора"}</h2>
                <p>Перегляд карти відкритий. Для запису в БД використовується ключ NAVIGATION_EDITOR_TOKEN.</p></div>
            {canEdit ? <button type="button" className={cls.secondaryButton} onClick={() => {
                if (!discardAllowed()) return;
                try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* No session storage. */ }
                setToken(""); setStatus(current => current ? { ...current, canEdit: false } : null);
                setAccessOpen(false); setDraft(null); setSelected(null); setDirty(false); setPlacing(null);
            }}>Завершити редагування</button> : <form onSubmit={unlock} className={cls.keyForm}>
                <label className={cls.srOnly} htmlFor="navigation-editor-key">Ключ редактора</label>
                <input ref={keyRef} id="navigation-editor-key" type="password" autoComplete="off" required value={keyInput} onChange={event => setKeyInput(event.target.value)} placeholder="Уведіть ключ" />
                <button className={cls.primaryButton} type="submit" disabled={authBusy}>{authBusy ? "Перевіряємо…" : "Підключити"}</button>
            </form>}
            <button type="button" className={cls.iconButton} onClick={() => setAccessOpen(false)} aria-label="Закрити доступ редактора"><FiX /></button>
            {authError && <p className={cls.formError} role="alert">{authError}</p>}
        </section>}

        <section className={cls.stats} aria-label="Наповнення бази">
            <div><strong>{count(status?.roads)}</strong><span>дорожніх сегментів</span></div>
            <div><strong>{count(status?.landmarks)}</strong><span>орієнтирів у базі</span></div>
            <div><strong>{count(status?.verified)}</strong><span>перевірених орієнтирів</span></div>
            <div className={cls.connection}><span className={`${cls.statusDot} ${status?.ready ? cls.connected : ""}`} />
                <span>{status?.ready ? "PostGIS підключено" : statusError ? "БД недоступна" : "Підключаємося…"}<small>{canEdit ? "Режим редактора" : "Режим перегляду"}</small></span></div>
        </section>

        {(statusError || dataError || mapError) && <div className={cls.errorBanner} role="alert">
            <p>{statusError || dataError || mapError}</p>
            <button type="button" className={cls.textButton} onClick={() => { setMapError(""); setRefresh(value => value + 1); }}><FiRefreshCw /> Повторити</button>
        </div>}

        <section className={cls.workspace} aria-label="Редактор карти">
            <div className={cls.toolbar}>
                <div className={cls.streetSearch}>
                    <FiSearch aria-hidden="true" />
                    <input aria-label="Пошук вулиці" value={query} maxLength={100} onChange={event => setQuery(event.target.value)} placeholder="Знайти вулицю в Києві…" />
                    {query && <button type="button" className={cls.iconButton} onClick={() => setQuery("")} aria-label="Очистити пошук"><FiX /></button>}
                    {query.trim().length >= 3 && <div className={cls.searchResults}>
                        {searchLoading ? <p role="status">Шукаємо…</p> : searchError ? <p role="alert">{searchError}</p> : streets.length ? streets.map(street =>
                            <button type="button" key={`${street.name}:${street.bounds.join(",")}`} onClick={() => { setCommand({ sequence: Date.now(), bounds: street.bounds }); setQuery(""); }}><FiMapPin /><span>{street.name}<small>{((street.bounds[1] + street.bounds[3]) / 2).toFixed(3)}, {((street.bounds[0] + street.bounds[2]) / 2).toFixed(3)}</small></span></button>
                        ) : <p>Вулиць з такою назвою не знайдено.</p>}
                    </div>}
                </div>
                <div className={cls.layerControls}>
                    <FiLayers aria-hidden="true" />
                    <label><input type="checkbox" checked={showRoads} onChange={event => setShowRoads(event.target.checked)} /> Дороги</label>
                    <label><input type="checkbox" checked={showVerified} onChange={event => setShowVerified(event.target.checked)} /> Перевірені прив’язки</label>
                    <button type="button" className={cls.iconButton} title="Показати Київ" aria-label="Показати Київ" onClick={() => setCommand({ sequence: Date.now(), center: KYIV_CENTER, zoom: 11.5 })}><FiCrosshair /></button>
                </div>
            </div>

            <div className={cls.mapPanel}>
                <NavigationMap data={data} draft={draft} canEdit={canEdit && !saving} placing={!!placing} showRoads={showRoads}
                    showVerified={showVerified} command={command} onViewport={onViewport} onPosition={selectPosition}
                    onLandmark={id => void selectLandmark(id)} onRoad={selectRoad} onError={setMapError} />
                {placing && <div className={cls.placementPrompt} role="status"><FiMapPin />
                    <span>{placing === "create" ? "Натисніть на карту, щоб додати орієнтир" : "Натисніть на нове місце орієнтира"}</span>
                    <button type="button" onClick={() => setPlacing(null)}>Скасувати</button>
                </div>}
                {loading && <span className={cls.mapBusy} role="status">Оновлюємо дані…</span>}
                {message && <div className={cls.mapMessage} role="status">{message}</div>}
                <div className={cls.mapCaption}>{viewport.zoom < ROAD_MIN_ZOOM ? "Наблизьте карту до масштабу 14+, щоб побачити дорожні ділянки" : "Натисніть на орієнтир, щоб відкрити картку"}</div>
            </div>

            <aside className={cls.sidebar} aria-label="Орiєнтири та їхні властивості">
                {selectionLoading && <p className={cls.panelLoading} role="status">Завантажуємо картку…</p>}
                {draft ? <LandmarkEditor key={selected?.id ?? "new"} draft={draft} selected={selected} canEdit={canEdit}
                    saving={saving || selectionLoading} dirty={dirty} nearby={nearby} nearbyLoading={nearbyLoading} nearbyError={nearbyError}
                    error={formError} onChange={changeDraft} onClose={closeEditor} onMove={() => setPlacing("move")}
                    onSave={() => void save()} onDelete={() => void remove()} onUnlock={() => setAccessOpen(true)} /> : <div className={cls.catalog}>
                    <div className={cls.panelHeading}><div><p className={cls.eyebrow}>У видимій області</p><h2>Орієнтири <span>{count(data.landmarks.features.length)}</span></h2></div><FiMapPin /></div>
                    <div className={cls.filters}>
                        <label className={cls.field}>Тип орієнтира
                            <select value={kind} onChange={event => setKind(event.target.value as LandmarkKind | "")}>
                                <option value="">Усі типи</option>{LANDMARK_KINDS.map(kind => <option key={kind} value={kind}>{KIND_LABELS[kind]}</option>)}
                            </select>
                        </label>
                        <label className={cls.field}>Статус перевірки
                            <select value={verification} onChange={event => setVerification(event.target.value)}><option value="">Усі орієнтири</option><option value="true">Перевірені</option><option value="false">Потребують перевірки</option></select>
                        </label>
                        <input aria-label="Фільтрувати список орієнтирів" value={listQuery} onChange={event => setListQuery(event.target.value)} placeholder="Назва або код у цьому вікні…" />
                    </div>
                    {(data.landmarksTruncated || data.roadsTruncated) && <p className={cls.notice}>Об’єктів забагато для одного вікна. Наблизьте карту — буде завантажено детальніші дані.</p>}
                    <div className={cls.landmarkList}>
                        {visibleLandmarks.slice(0, 80).map(landmark => <button type="button" key={landmark.id} className={cls.landmarkItem} onClick={() => void selectLandmark(landmark.id, true)}>
                            <span className={`${cls.kindDot} ${cls[landmark.kind]}`} />
                            <span><strong>{landmark.title || `${KIND_LABELS[landmark.kind]}${landmark.signCode ? ` ${landmark.signCode}` : ""}`}</strong>
                                <small>{landmark.bindings[0]?.roadName || `${landmark.latitude.toFixed(5)}, ${landmark.longitude.toFixed(5)}`}</small>
                                <small className={landmark.verified ? cls.verifiedLabel : ""}>{landmark.verified ? "Перевірено" : "Потребує перевірки"} · {landmark.source === "osm" ? "OSM" : "Вручну"}</small></span>
                            {landmark.verified && <FiCheck />}
                        </button>)}
                        {!visibleLandmarks.length && !loading && <div className={cls.emptyState}><FiMapPin /><h3>Тут поки немає орієнтирів</h3><p>Перемістіть карту, змініть фільтри або додайте новий об’єкт.</p></div>}
                        {visibleLandmarks.length > 80 && <p className={cls.listFooter}>Показано 80 із {count(visibleLandmarks.length)}. Наблизьте карту або уточніть фільтр.</p>}
                    </div>
                </div>}
            </aside>

            <div className={cls.legend}>
                {LANDMARK_KINDS.map(kind => <span key={kind}><i className={`${cls.kindDot} ${cls[kind]}`} />{KIND_LABELS[kind]}</span>)}
                <span className={cls.coverageLegend}><i />Ділянка з перевіреним орієнтиром</span>
            </div>
        </section>

        <section className={cls.notes}>
            <div><span className={cls.step}>01</span><h2>Знайдіть ділянку</h2><p>Пошук за назвою вулиці та масштабування карти. Геолокація телефону для цього не потрібна.</p></div>
            <div><span className={cls.step}>02</span><h2>Додайте орієнтир</h2><p>Уточніть тип, координати, видимість і напрямок руху. Зв’яжіть об’єкт із дорожньою ділянкою.</p></div>
            <div><span className={cls.step}>03</span><h2>Перевірте дані</h2><p>Позначайте підтверджені об’єкти. Підсвічена ділянка означає наявність перевіреного орієнтира, а не повне покриття вулиці.</p></div>
        </section>
        <div className={cls.pageFooter}>
            <p>{status?.lastImport ? `Останній імпорт: ${status.lastImport.name} · ${new Date(status.lastImport.completedAt).toLocaleDateString("uk-UA")}` : "Джерело дорожніх даних: OpenStreetMap. Розмітка зберігається в PostgreSQL / PostGIS."}</p>
            <Button href="/" invert ariaLabel="На головну UData"><FiArrowLeft /> На головну</Button>
        </div>
    </main>;
}
